/**
 * ClientDetail — single client view.
 *
 * Two columns:
 *   LEFT: Contact card (edit-in-place), metrics, notes timeline
 *   RIGHT: Invoices linked to this client + "New invoice for this client" CTA
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Phone, Globe, MapPin, Pencil, Trash2, Save, X,
  Plus, MessageSquare, FileText, ExternalLink, Tag as TagIcon,
} from 'lucide-react';
import { GlassCard } from '@/components/dashboard/dashboard-ui';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtMoney = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;
const fmtDate  = (iso) => { try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return '—'; } };
const fmtShort = (iso) => { try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return '—'; } };

const STATUS_COLORS = {
  draft:   'bg-white/10 text-white/70',
  sent:    'bg-cyan-500/15 text-cyan-200 border border-cyan-500/30',
  open:    'bg-cyan-500/15 text-cyan-200 border border-cyan-500/30',
  paid:    'bg-emerald-500/15 text-emerald-200 border border-emerald-500/30',
  overdue: 'bg-rose-500/15 text-rose-200 border border-rose-500/30',
  voided:  'bg-white/5 text-white/40 line-through',
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [tagDraft, setTagDraft] = useState('');

  const load = useCallback(async () => {
    try {
      const [c, inv] = await Promise.all([
        axios.get(`${API}/builder/crm/clients/${id}`, { headers: auth() }),
        axios.get(`${API}/builder/crm/clients/${id}/invoices`, { headers: auth() }),
      ]);
      setClient(c.data);
      setInvoices(inv.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load client');
      navigate('/website-builder/invoicing/clients');
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(client))); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(null); };
  const saveEdit = async () => {
    try {
      const { data } = await axios.put(`${API}/builder/crm/clients/${id}`, draft, { headers: auth() });
      setClient(data);
      setEditing(false);
      toast.success('Client updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async () => {
    if (!(await themedConfirm('Delete this client? Their invoices stay but will no longer be linked.'))) return;
    try {
      await axios.delete(`${API}/builder/crm/clients/${id}`, { headers: auth() });
      toast.success('Client deleted');
      navigate('/website-builder/invoicing/clients');
    } catch (e) { toast.error('Could not delete'); }
  };

  const addNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    try {
      const { data: note } = await axios.post(`${API}/builder/crm/clients/${id}/notes`, { text }, { headers: auth() });
      setClient(c => ({ ...c, notes: [note, ...(c.notes || [])] }));
      setNoteDraft('');
    } catch (e) { toast.error('Could not save note'); }
  };
  const delNote = async (noteId) => {
    try {
      await axios.delete(`${API}/builder/crm/clients/${id}/notes/${noteId}`, { headers: auth() });
      setClient(c => ({ ...c, notes: (c.notes || []).filter(n => n.id !== noteId) }));
    } catch (e) { toast.error('Could not delete note'); }
  };

  const addTag = async () => {
    const tag = tagDraft.trim().toLowerCase();
    if (!tag) return;
    try {
      await axios.post(`${API}/builder/crm/clients/${id}/tags`, { tag }, { headers: auth() });
      setClient(c => ({ ...c, tags: [...new Set([...(c.tags || []), tag])] }));
      setTagDraft('');
    } catch (e) { toast.error('Could not add tag'); }
  };
  const delTag = async (tag) => {
    try {
      await axios.delete(`${API}/builder/crm/clients/${id}/tags/${encodeURIComponent(tag)}`, { headers: auth() });
      setClient(c => ({ ...c, tags: (c.tags || []).filter(x => x !== tag) }));
    } catch (e) { toast.error('Could not remove tag'); }
  };

  if (loading || !client) {
    return <div className="min-h-full px-6 py-10 max-w-5xl mx-auto text-white/40 text-center">Loading…</div>;
  }
  const m = client.metrics || {};

  return (
    <div className="min-h-full px-4 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-6xl mx-auto space-y-6" data-testid="client-detail-page">
      <div>
        <Link to="/website-builder/invoicing/clients" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-xs mb-2" data-testid="back-to-clients">
          <ArrowLeft className="w-3.5 h-3.5" /> Clients
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-violet-300/80 mb-1">Client</div>
            <h1 className="text-4xl font-bold text-white tracking-tight" data-testid="client-name-heading">
              {client.business_name || client.name || 'Unnamed client'}
            </h1>
            {client.business_name && client.name && (
              <div className="text-white/50 mt-1">{client.name}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/website-builder/invoicing/invoices/new?client=${client.id}`}
              data-testid="new-invoice-for-client"
              className="px-4 py-2 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold inline-flex items-center gap-2 shadow-[0_8px_24px_-8px_rgba(139,92,246,0.6)]">
              <FileText className="w-4 h-4" /> New invoice
            </Link>
            {!editing && (
              <button onClick={startEdit} data-testid="edit-client" className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 text-sm inline-flex items-center gap-1.5">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            )}
            <button onClick={remove} data-testid="delete-client" className="p-2 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-300">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="client-metrics">
        <Stat label="Invoices" value={m.invoice_count || 0} />
        <Stat label="Paid" value={fmtMoney(m.total_paid_cents)} accent="emerald" />
        <Stat label="Outstanding" value={fmtMoney(m.outstanding_cents)} accent="amber" />
        <Stat label="Lifetime" value={fmtMoney(m.total_invoiced_cents)} accent="violet" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT — contact + notes */}
        <div className="lg:col-span-1 space-y-4">
          <GlassCard className="p-5 space-y-3" data-testid="contact-card">
            {!editing ? (
              <>
                <div className="text-xs uppercase tracking-[0.14em] text-white/40">Contact</div>
                <Row icon={Mail}  v={client.email}   placeholder="No email" />
                <Row icon={Phone} v={client.phone}   placeholder="No phone" />
                <Row icon={Globe} v={client.website} placeholder="No website" link />
                <Row icon={MapPin} multiline v={[client.billing_address, [client.city, client.state, client.zip].filter(Boolean).join(', '), client.country].filter(Boolean).join('\n')} placeholder="No billing address" />
                <div>
                  <div className="text-xs text-white/40 mb-1">Status</div>
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${
                    client.status === 'active'   ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-500/30' :
                    client.status === 'lead'     ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-500/30' :
                                                   'bg-white/5 text-white/40'
                  }`}>{client.status}</span>
                </div>
                <div>
                  <div className="text-xs text-white/40 mb-1.5 inline-flex items-center gap-1"><TagIcon className="w-3 h-3" /> Tags</div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(client.tags || []).map(t => (
                      <span key={t} className="px-2 py-0.5 rounded bg-violet-500/15 border border-violet-400/30 text-violet-200 text-xs inline-flex items-center gap-1.5" data-testid={`tag-pill-${t}`}>
                        {t}<button onClick={() => delTag(t)} className="text-violet-300/60 hover:text-white"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                    {(!client.tags || client.tags.length === 0) && <span className="text-white/30 text-xs">No tags yet</span>}
                  </div>
                  <div className="flex gap-1.5">
                    <input value={tagDraft} onChange={e => setTagDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Add tag" data-testid="add-tag-input"
                      className="flex-1 px-2.5 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs placeholder-white/25 outline-none focus:border-violet-400" />
                    <button onClick={addTag} data-testid="add-tag-btn" className="px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/15 text-white text-xs">Add</button>
                  </div>
                </div>
              </>
            ) : (
              <EditForm draft={draft} setDraft={setDraft} onCancel={cancelEdit} onSave={saveEdit} />
            )}
          </GlassCard>

          {/* Notes */}
          <GlassCard className="p-5" data-testid="notes-card">
            <div className="text-xs uppercase tracking-[0.14em] text-white/40 mb-3 inline-flex items-center gap-1.5">
              <MessageSquare className="w-3 h-3" /> Notes
            </div>
            <div className="space-y-2 mb-3">
              <textarea value={noteDraft} onChange={e => setNoteDraft(e.target.value)} rows={3} placeholder="Log a conversation, deal status, follow-up reminder…" data-testid="note-textarea"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 outline-none focus:border-violet-400" />
              <button onClick={addNote} disabled={!noteDraft.trim()} data-testid="add-note-submit"
                className="px-3 py-1.5 rounded-md bg-violet-500 hover:bg-violet-400 disabled:opacity-50 text-white text-xs font-semibold inline-flex items-center gap-1.5">
                <Plus className="w-3 h-3" /> Add note
              </button>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
              {(client.notes || []).length === 0 && <div className="text-white/30 text-xs">No notes yet.</div>}
              {(client.notes || []).map(n => (
                <div key={n.id} className="pl-3 border-l-2 border-violet-500/30 group" data-testid={`note-${n.id}`}>
                  <div className="text-sm text-white/90 whitespace-pre-wrap">{n.text}</div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-[10px] text-white/30">{fmtDate(n.created_at)}</div>
                    <button onClick={() => delNote(n.id)} className="text-white/20 group-hover:text-rose-300 text-[10px] inline-flex items-center gap-1"
                      data-testid={`note-delete-${n.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>

        {/* RIGHT — invoices */}
        <div className="lg:col-span-2">
          <GlassCard className="p-5" data-testid="invoices-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-white/40 mb-0.5">Invoices</div>
                <div className="text-white text-base font-semibold">{invoices.length} total</div>
              </div>
              <Link to={`/website-builder/invoicing/invoices/new?client=${client.id}`} className="text-violet-300 hover:text-violet-200 text-xs inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Create
              </Link>
            </div>
            {invoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-10 h-10 mx-auto text-white/15 mb-3" />
                <div className="text-white/50 text-sm">No invoices yet for this client.</div>
                <Link to={`/website-builder/invoicing/invoices/new?client=${client.id}`} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-violet-500 hover:bg-violet-400 text-white text-xs font-semibold">
                  <Plus className="w-3 h-3" /> Create the first invoice
                </Link>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-wider text-white/40">
                  <div className="col-span-3">Number</div>
                  <div className="col-span-3">Date</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-3 text-right">Total</div>
                  <div className="col-span-1"></div>
                </div>
                {invoices.map(inv => (
                  <div key={inv.id} className="grid grid-cols-12 px-3 py-2.5 rounded-lg hover:bg-white/5 items-center text-sm" data-testid={`inv-row-${inv.id}`}>
                    <div className="col-span-3 text-white font-mono text-xs">{(inv.meta?.number) || inv.id.slice(0, 8).toUpperCase()}</div>
                    <div className="col-span-3 text-white/60 text-xs">{fmtShort(inv.created_at)}</div>
                    <div className="col-span-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${STATUS_COLORS[inv.status] || STATUS_COLORS.draft}`}>{inv.status}</span>
                    </div>
                    <div className="col-span-3 text-right text-white tabular-nums">${(Number(inv.total) || 0).toFixed(2)}</div>
                    <div className="col-span-1 flex justify-end">
                      {inv.stripe_invoice_url && (
                        <a href={inv.stripe_invoice_url} target="_blank" rel="noreferrer" className="text-white/40 hover:text-white"><ExternalLink className="w-3.5 h-3.5" /></a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent = 'white' }) => {
  const map = {
    white: 'text-white', emerald: 'text-emerald-300', amber: 'text-amber-300', violet: 'text-violet-300',
  };
  return (
    <GlassCard className="p-4">
      <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
      <div className={`text-xl font-bold mt-1 ${map[accent]}`}>{value}</div>
    </GlassCard>
  );
};

const Row = ({ icon: Icon, v, placeholder, multiline, link }) => (
  <div className="flex items-start gap-2.5 text-sm">
    <Icon className="w-4 h-4 text-white/40 mt-0.5 flex-shrink-0" />
    {v ? (
      link
        ? <a href={v.startsWith('http') ? v : `https://${v}`} target="_blank" rel="noreferrer" className="text-violet-200 hover:text-violet-100 truncate">{v}</a>
        : <span className={`text-white/80 ${multiline ? 'whitespace-pre-line' : 'truncate'}`}>{v}</span>
    ) : <span className="text-white/30 text-xs italic">{placeholder}</span>}
  </div>
);

function EditForm({ draft, setDraft, onCancel, onSave }) {
  const upd = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-[0.14em] text-white/40 mb-2">Edit contact</div>
      <Inp label="Contact name"  v={draft.name}            on={v => upd('name', v)} testid="edit-name" />
      <Inp label="Business name" v={draft.business_name}   on={v => upd('business_name', v)} testid="edit-business" />
      <Inp label="Email" type="email" v={draft.email}      on={v => upd('email', v)} testid="edit-email" />
      <Inp label="Phone"         v={draft.phone}           on={v => upd('phone', v)} testid="edit-phone" />
      <Inp label="Website"       v={draft.website}         on={v => upd('website', v)} testid="edit-website" />
      <Inp label="Billing"       v={draft.billing_address} on={v => upd('billing_address', v)} testid="edit-billing" multiline />
      <div className="grid grid-cols-3 gap-2">
        <Inp small label="Country" v={draft.country} on={v => upd('country', v)} testid="edit-country" />
        <Inp small label="State"   v={draft.state}   on={v => upd('state', v)}   testid="edit-state" />
        <Inp small label="Zip"     v={draft.zip}     on={v => upd('zip', v)}     testid="edit-zip" />
      </div>
      <div>
        <div className="text-xs text-white/40 mb-1">Status</div>
        <div className="flex gap-2">
          {['active', 'lead', 'archived'].map(s => (
            <button key={s} onClick={() => upd('status', s)} data-testid={`edit-status-${s}`}
              className={`px-3 py-1 rounded-lg text-xs capitalize ${draft.status === s ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white border border-white/10'}`}>{s}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-3">
        <button onClick={onSave} data-testid="save-edit" className="flex-1 px-3 py-2 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5"><Save className="w-3.5 h-3.5" /> Save</button>
        <button onClick={onCancel} data-testid="cancel-edit" className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-sm">Cancel</button>
      </div>
    </div>
  );
}
const Inp = ({ label, v, on, testid, type = 'text', multiline, small }) => (
  <div>
    <div className={`text-[10px] text-white/40 mb-0.5 ${small ? '' : ''}`}>{label}</div>
    {multiline ? (
      <textarea value={v ?? ''} onChange={e => on(e.target.value)} rows={2} data-testid={testid}
        className="w-full px-2.5 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-violet-400 resize-y" />
    ) : (
      <input type={type} value={v ?? ''} onChange={e => on(e.target.value)} data-testid={testid}
        className="w-full px-2.5 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-violet-400" />
    )}
  </div>
);
